import type { BadgeProps } from '../components/SubscriptionAppBadge';
import { orderChannels } from '../data/mockContactOrders';
import type { ContactOrderRecord, OrderChannel, OrderChannelGroup } from '../data/mockContactOrders';

export function channelGroup(channel: OrderChannel): OrderChannelGroup {
  return orderChannels.find(item => item.channel === channel)?.group ?? 'one-time';
}

// Club channels share the blue badge, one-time channels the grey one, so the group reads at a glance
export function channelBadgeVariant(channel: OrderChannel): NonNullable<BadgeProps['variant']> {
  return channelGroup(channel) === 'club' ? 'info' : 'default';
}

export function orderTotal(order: ContactOrderRecord): number {
  return order.items.reduce((total, item) => total + item.price * item.quantity, 0);
}

export interface ChannelSummary {
  clubCount: number;
  oneTimeCount: number;
  clubSpend: number;
  oneTimeSpend: number;
  byChannel: {
    channel: OrderChannel;
    group: OrderChannelGroup;
    count: number;
  }[];
}

export function summarizeChannels(orders: ContactOrderRecord[]): ChannelSummary {
  const summary: ChannelSummary = {
    clubCount: 0,
    oneTimeCount: 0,
    clubSpend: 0,
    oneTimeSpend: 0,
    byChannel: orderChannels.map(item => ({
      channel: item.channel,
      group: item.group,
      count: 0
    }))
  };
  orders.forEach(order => {
    const isClub = channelGroup(order.channel) === 'club';
    const total = orderTotal(order);
    if (isClub) {
      summary.clubCount += 1;
      summary.clubSpend += total;
    } else {
      summary.oneTimeCount += 1;
      summary.oneTimeSpend += total;
    }
    const entry = summary.byChannel.find(item => item.channel === order.channel);
    if (entry) entry.count += 1;
  });
  return summary;
}

// Orders tab filter: everything, a group, or one channel
export type OrderChannelFilter = 'all' | 'group:club' | 'group:one-time' | OrderChannel;

export function matchesChannelFilter(channel: OrderChannel, filter: OrderChannelFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'group:club') return channelGroup(channel) === 'club';
  if (filter === 'group:one-time') return channelGroup(channel) === 'one-time';
  return channel === filter;
}
